import { get, post } from "./client";

/**
 * Client for the DEV-2803 office identity backfill endpoints. A UI bug wiped the office
 * identity fields on address-book offices; the scan lives on wp-syncer (it reads
 * WordPress for last-good values) and the apply on address-book (it owns the office rows
 * being written).
 *
 * The two paths deliberately carry different service segments — omitting the
 * segment 404s at the gateway.
 */

export const BACKFILL_FIELDS = [
  "slug",
  "country_term_slug",
  "country_slug",
  "country_parent_slug",
  "country_parent_name",
  "office_ref",
  "crm_entity_number",
] as const;

export type BackfillField = (typeof BACKFILL_FIELDS)[number];

/**
 * Rows per `apply` request. Callers with a bigger batch must split it and send
 * the chunks one after another — the response is the only copy of each row's
 * pre-update state, so a gateway timeout on one oversized request would lose
 * the rollback data for the whole run.
 */
export const APPLY_CHUNK_SIZE = 100;

/** One wiped field on one office, with WordPress's proposal for it. */
export type ScanRow = {
  officeId: string;
  officeName: string;
  field: BackfillField;
  current: string | null;
  /** Null when WordPress holds no last-good value — the row cannot be backfilled. */
  proposed: string | null;
};

export type ScanCounts = {
  scanned: number;
  missingByField: Record<string, number>;
  unresolved: number;
};

export type ScanResponse = {
  rows: ScanRow[];
  counts: ScanCounts;
  sourceInfo?: {
    source: "wordpress";
    officesRequested: number;
    officesResolved: number;
  };
};

export type ApplyMode = "backfill" | "restore";

export type ApplyRow = {
  officeId: string;
  field: BackfillField;
  value: string;
};

export type ApplyRowStatus = "applied" | "noop" | "skipped_not_empty" | "invalid" | "error";

/**
 * One result per submitted row, in the SAME ORDER as the request — merge by
 * index, since officeId+field is the only other key and a malformed upload
 * could repeat it.
 */
export type ApplyRowResult = {
  officeId: string;
  field: BackfillField;
  status: ApplyRowStatus;
  /** Pre-update DB state. What the rollback CSV is built from. */
  previous: string | null;
  message?: string;
};

export type ApplyResponse = {
  results: ApplyRowResult[];
  counts: Record<string, number>;
};

export const officeIdentityBackfillService = {
  async scan(): Promise<ScanResponse> {
    return get<ScanResponse>("/v1/wp-syncer/office-identity-backfill");
  },

  async apply(rows: ApplyRow[], mode: ApplyMode): Promise<ApplyResponse> {
    return post<ApplyResponse>("/v1/address-book/office-identity-backfill/apply", { mode, rows });
  },
};
